import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, fmt } from "@/api";
import { Package, User, MapPin, Heart } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Account() {
  const { user, authLoading, logout, setUser } = useApp();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [wl, setWl] = useState([]);
  const [profile, setProfile] = useState({ name:"", phone:"" });
  const [newAddr, setNewAddr] = useState({ full_name:"", phone:"", line1:"", line2:"", landmark:"", city:"", state:"Maharashtra", pincode:"", is_default:false });

  useEffect(()=>{
    if (!user) return;
    setProfile({ name:user.name, phone:user.phone||"" });
    api.get("/orders/my").then(({data})=>setOrders(data));
    api.get("/addresses").then(({data})=>setAddresses(data));
    api.get("/wishlist").then(({data})=>setWl(data));
  }, [user]);

  if (authLoading) return <div className="p-20 text-center text-muted-foreground">Loading...</div>;
  if (!user) return <div className="p-20 text-center"><h1 className="font-serif text-2xl">Please login</h1><Link to="/login" className="pk-btn-primary mt-4 inline-flex">Login</Link></div>;

  const saveProfile = async () => { await api.put("/auth/profile", profile); setUser({...user, ...profile}); toast.success("Profile updated"); };
  const addAddress = async () => { const {data} = await api.post("/addresses", newAddr); setAddresses([...addresses, data]); toast.success("Address added"); setNewAddr({...newAddr, line1:"", line2:"", pincode:"", landmark:""}); };
  const delAddress = async (id) => { await api.delete(`/addresses/${id}`); setAddresses(addresses.filter(a=>a.id!==id)); };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="section-label">My Account</div>
      <h1 className="font-serif text-4xl mt-2">Namaste, {user.name.split(" ")[0]}</h1>

      <Tabs defaultValue="orders" className="mt-8">
        <TabsList data-testid="account-tabs" className="bg-muted">
          <TabsTrigger value="orders" data-testid="tab-orders"><Package className="w-4 h-4 mr-2"/>Orders</TabsTrigger>
          <TabsTrigger value="addresses" data-testid="tab-addresses"><MapPin className="w-4 h-4 mr-2"/>Addresses</TabsTrigger>
          <TabsTrigger value="wishlist" data-testid="tab-wishlist"><Heart className="w-4 h-4 mr-2"/>Wishlist</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-2"/>Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          {orders.length===0 ? <div className="text-muted-foreground">No orders yet.</div> :
           <div className="space-y-3">
             {orders.map(o => (
               <Link key={o.id} to={`/order/${o.id}`} className="block p-4 rounded-xl border border-border bg-white hover:border-primary transition-colors" data-testid={`order-row-${o.order_no}`}>
                 <div className="flex justify-between items-center flex-wrap gap-2">
                   <div>
                     <div className="font-medium">{o.order_no}</div>
                     <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} items</div>
                   </div>
                   <div className="text-sm px-3 py-1 rounded-full bg-secondary/30 capitalize">{o.status}</div>
                   <div className="font-semibold text-primary">{fmt(o.total)}</div>
                 </div>
               </Link>
             ))}
           </div>}
        </TabsContent>

        <TabsContent value="addresses" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            {addresses.map(a=>(
              <div key={a.id} className="rounded-xl border border-border p-4 bg-white">
                <div className="font-medium">{a.full_name} · {a.phone}</div>
                <div className="text-sm text-muted-foreground mt-1">{a.line1}, {a.line2} {a.landmark && `(${a.landmark})`}</div>
                <div className="text-sm text-muted-foreground">{a.city}, {a.state} - {a.pincode}</div>
                <button onClick={()=>delAddress(a.id)} className="mt-3 text-xs text-primary">Remove</button>
              </div>
            ))}
            <div className="rounded-xl border-2 border-dashed border-border p-4 bg-white">
              <div className="font-medium mb-3">Add new address</div>
              <div className="grid grid-cols-2 gap-2">
                <input className="pk-input" placeholder="Name" value={newAddr.full_name} onChange={e=>setNewAddr({...newAddr, full_name:e.target.value})}/>
                <input className="pk-input" placeholder="Phone" value={newAddr.phone} onChange={e=>setNewAddr({...newAddr, phone:e.target.value})}/>
                <input className="pk-input col-span-2" placeholder="Address line 1" value={newAddr.line1} onChange={e=>setNewAddr({...newAddr, line1:e.target.value})}/>
                <input className="pk-input" placeholder="City" value={newAddr.city} onChange={e=>setNewAddr({...newAddr, city:e.target.value})}/>
                <input className="pk-input" placeholder="Pincode" value={newAddr.pincode} onChange={e=>setNewAddr({...newAddr, pincode:e.target.value})}/>
              </div>
              <button onClick={addAddress} className="pk-btn-primary w-full mt-3 py-2">Save Address</button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="wishlist" className="mt-6">
          {wl.length===0 ? <div className="text-muted-foreground">No items in wishlist.</div> :
           <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
             {wl.map(p => (
               <Link key={p.id} to={`/product/${p.slug}`} className="pk-card p-3">
                 <img src={p.image} className="w-full aspect-square object-cover rounded-lg"/>
                 <div className="mt-2 font-medium text-sm line-clamp-2">{p.name}</div>
                 <div className="text-primary font-semibold mt-1">{fmt(p.price)}</div>
               </Link>
             ))}
           </div>}
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <div className="max-w-md space-y-3 p-6 rounded-xl border border-border bg-white">
            <input className="pk-input" placeholder="Name" value={profile.name} onChange={e=>setProfile({...profile, name:e.target.value})}/>
            <input className="pk-input" placeholder="Phone" value={profile.phone} onChange={e=>setProfile({...profile, phone:e.target.value})}/>
            <input className="pk-input" placeholder="Email" value={user.email} disabled/>
            <button onClick={saveProfile} className="pk-btn-primary w-full">Save Profile</button>
            <button onClick={logout} className="pk-btn-secondary w-full mt-2">Logout</button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
